// src/components/AppHeader.jsx
// Top app header — brand · org name · search · location · notifications · profile.
//
// CLAUDE CODE: prepend `import React from "react";` etc, replace
// the `const PPIcons = window.PPIcons; ...` lines with real imports,
// and add `export default X;` at the bottom (replacing `window.X = X`).

import React, { useState, useRef, useEffect } from "react";
import * as PPIcons from "./_icons";
import { Avatar, Chip } from "./_primitives";
import PinchIcon from "./PinchIcon";

function AppHeader({
  productName = "Pinch Point",
  logoUrl,
  orgName,
  locations = [],
  selectedLocationId,
  onSelectLocation,
  unreadCount = 0,
  onOpenNotifications,
  userName,
  isAdmin,
  isSuperUser,
  dark,
  onToggleDark,
  onLogout,
}) {
  const [profileOpen, setProfileOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!profileOpen) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setProfileOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [profileOpen]);

  return (
    <header className="pp-app-header">
      <div className="pp-brand">
        {logoUrl ? (
          <img src={logoUrl} alt={productName} style={{ height: 28 }} />
        ) : (
          <>
            <span className="pp-brand-mark"><PinchIcon size={16} /></span>
            <span>{productName}</span>
          </>
        )}
      </div>

      {orgName && <span className="pp-org-name">{orgName}</span>}

      <div style={{ flex: 1 }} />

      {locations.length > 0 && (
        <select
          className="pp-select"
          style={{ height: 32, width: 180, fontSize: 13 }}
          value={selectedLocationId || ""}
          onChange={e => onSelectLocation && onSelectLocation(e.target.value || null)}
          aria-label="Filter by location"
        >
          <option value="">All locations</option>
          {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
      )}

      <button className="pp-icon-btn" aria-label="Search">
        <PPIcons.IconSearch />
      </button>

      <button className="pp-icon-btn" aria-label="Notifications" onClick={onOpenNotifications}>
        <PPIcons.IconBell />
        {unreadCount > 0 && (
          <span className="pp-icon-dot">{unreadCount > 9 ? "9+" : unreadCount}</span>
        )}
      </button>

      <div style={{ position: "relative" }} ref={ref}>
        <button
          onClick={() => setProfileOpen(o => !o)}
          aria-label="Account menu"
          style={{ border: 0, background: "transparent", padding: 0, cursor: "pointer" }}
        >
          <Avatar name={userName} brand />
        </button>

        {profileOpen && (
          <div style={{
            position: "absolute", top: 42, right: 0, zIndex: 70,
            width: 240, background: "var(--bg-elevated)",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius)", boxShadow: "var(--shadow-pop)",
            padding: 6,
          }}>
            <div style={{ padding: "10px 12px 8px" }}>
              <div style={{ fontWeight: 700, fontSize: 13 }}>{userName}</div>
              {(isAdmin || isSuperUser) && (
                <div style={{ marginTop: 4 }}>
                  <Chip tone={isSuperUser ? "ai" : "category"} dot={false}>
                    {isSuperUser ? "Super user" : "Admin"}
                  </Chip>
                </div>
              )}
            </div>
            <hr className="pp-hr" />
            <MenuItem icon={<PPIcons.IconUser />} label="Personal information" />
            <MenuItem
              icon={dark ? <PPIcons.IconSun /> : <PPIcons.IconMoon />}
              label={dark ? "Light mode" : "Dark mode"}
              onClick={onToggleDark}
            />
            <MenuItem icon={<PPIcons.IconHelpCircle />} label="Tutorial" />
            <hr className="pp-hr" />
            <MenuItem icon={<PPIcons.IconLogOut />} label="Sign out" onClick={onLogout} danger />
          </div>
        )}
      </div>
    </header>
  );
}

function MenuItem({ icon, label, onClick, danger }) {
  return (
    <button
      onClick={onClick}
      style={{
        width: "100%", display: "flex", alignItems: "center", gap: 10,
        padding: "8px 12px", border: 0, background: "transparent",
        borderRadius: 6, cursor: "pointer", textAlign: "left",
        color: danger ? "var(--pp-crit)" : "var(--text)",
        fontSize: 13, fontWeight: 500,
      }}
      onMouseEnter={e => e.currentTarget.style.background = "var(--surface-2)"}
      onMouseLeave={e => e.currentTarget.style.background = "transparent"}
    >
      <span style={{ width: 14, display: "inline-flex", color: "var(--text-dim)" }}>{icon}</span>
      {label}
    </button>
  );
}

export default AppHeader;
